/**
 * BugReportContext — stan pływającego widgetu „Zgłoś błąd" (tenant scope, front-only).
 *
 * Widget ma trzy stany (`status`): `closed` (schowany), `open` (pełny panel) oraz
 * `minimized` (mały kafelek w rogu — DRAFT NIE GINIE). Trigger (robaczek) żyje w
 * topbarze, panel/kafelek renderuje `<BugReportWidget>` przez portal — dlatego stan
 * musi być współdzielony przez kontekst, a nie lokalny w którymś z tych miejsc.
 *
 * Na tym etapie to WYŁĄCZNIE UI/UX — „Wyślij" nie woła backendu (dojdzie osobno:
 * kto zgłosił, tenant, wersja, data + tabela zgłoszeń w panelu ITE). Kontekst trzyma
 * treść zgłoszenia (opis + zrzuty ekranu) i pilnuje cyklu życia `URL.createObjectURL`
 * dla podglądów (revoke przy usunięciu pojedynczego zrzutu, przy reset/close i przy
 * odmontowaniu), żeby nie wyciekały bloby.
 */

import {
  createContext,
  type ReactNode,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';

/** Maksymalna liczba zrzutów ekranu w jednym zgłoszeniu (wymóg produktowy). */
export const MAX_BUG_SCREENSHOTS = 5;

/** Dozwolone typy zrzutów — tylko rastry (SVG odrzucamy: może nieść skrypt). */
export const ALLOWED_SCREENSHOT_TYPES = new Set([
  'image/png',
  'image/jpeg',
  'image/gif',
  'image/webp',
]);

export interface BugScreenshot {
  /** Lokalny identyfikator (do klucza listy i usuwania). */
  id: string;
  file: File;
  /** `URL.createObjectURL(file)` — podgląd miniatury; revoke w cyklu życia kontekstu. */
  previewUrl: string;
}

export type BugReportStatus = 'closed' | 'open' | 'minimized';

export interface BugReportContextValue {
  status: BugReportStatus;
  message: string;
  screenshots: BugScreenshot[];
  /** Czy jest cokolwiek do wysłania (opis lub choć jeden zrzut). */
  hasDraft: boolean;
  open: () => void;
  minimize: () => void;
  restore: () => void;
  /** Zamyka widget i czyści draft (revoke wszystkich podglądów). */
  close: () => void;
  setMessage: (value: string) => void;
  /** Dokłada pliki (tylko obrazy), przycinając do `MAX_BUG_SCREENSHOTS`. Zwraca ile pominięto. */
  addScreenshots: (files: FileList | File[]) => {
    added: number;
    rejected: number;
    overLimit: number;
  };
  removeScreenshot: (id: string) => void;
}

const BugReportContext = createContext<BugReportContextValue | null>(null);

function makeId(): string {
  return `bug-shot-${Date.now()}-${Math.random().toString(36).slice(2, 8)}`;
}

export function BugReportProvider({ children }: { children: ReactNode }) {
  const [status, setStatus] = useState<BugReportStatus>('closed');
  const [message, setMessage] = useState('');
  const [screenshots, setScreenshots] = useState<BugScreenshot[]>([]);

  // Ref do aktualnej listy — żeby cleanup przy odmontowaniu revoke'ował bez zależności.
  const screenshotsRef = useRef<BugScreenshot[]>([]);
  screenshotsRef.current = screenshots;

  useEffect(() => {
    return () => {
      for (const shot of screenshotsRef.current) URL.revokeObjectURL(shot.previewUrl);
    };
  }, []);

  const open = useCallback(() => setStatus('open'), []);
  const minimize = useCallback(() => setStatus('minimized'), []);
  const restore = useCallback(() => setStatus('open'), []);

  const close = useCallback(() => {
    setStatus('closed');
    setMessage('');
    setScreenshots((prev) => {
      for (const shot of prev) URL.revokeObjectURL(shot.previewUrl);
      return [];
    });
  }, []);

  const addScreenshots = useCallback((files: FileList | File[]) => {
    const incoming = Array.from(files);
    let rejected = 0;
    // Tylko rastry — SVG odrzucamy (może nieść skrypt; zrzut ekranu to i tak raster).
    const images = incoming.filter((f) => {
      const ok = ALLOWED_SCREENSHOT_TYPES.has(f.type);
      if (!ok) rejected += 1;
      return ok;
    });

    // Wolne sloty liczymy SYNCHRONICZNIE z aktualnego stanu (ref), nie w updaterze
    // setState — inaczej `overLimit` byłby liczony po returnie i zawsze == 0.
    const freeSlots = Math.max(0, MAX_BUG_SCREENSHOTS - screenshotsRef.current.length);
    const accepted = images.slice(0, freeSlots);
    const overLimit = images.length - accepted.length;
    const mapped = accepted.map((file) => ({
      id: makeId(),
      file,
      previewUrl: URL.createObjectURL(file),
    }));
    if (mapped.length > 0) setScreenshots((prev) => [...prev, ...mapped]);

    return { added: accepted.length, rejected, overLimit };
  }, []);

  const removeScreenshot = useCallback((id: string) => {
    setScreenshots((prev) => {
      const target = prev.find((s) => s.id === id);
      if (target) URL.revokeObjectURL(target.previewUrl);
      return prev.filter((s) => s.id !== id);
    });
  }, []);

  const value = useMemo<BugReportContextValue>(
    () => ({
      status,
      message,
      screenshots,
      hasDraft: message.trim().length > 0 || screenshots.length > 0,
      open,
      minimize,
      restore,
      close,
      setMessage,
      addScreenshots,
      removeScreenshot,
    }),
    [
      status,
      message,
      screenshots,
      open,
      minimize,
      restore,
      close,
      addScreenshots,
      removeScreenshot,
    ],
  );

  return <BugReportContext.Provider value={value}>{children}</BugReportContext.Provider>;
}

export function useBugReport(): BugReportContextValue {
  const ctx = useContext(BugReportContext);
  if (!ctx) {
    throw new Error('useBugReport musi być użyty wewnątrz <BugReportProvider>');
  }
  return ctx;
}
